import React, { CSSProperties, memo, Suspense, useState } from 'react';
import { useAdminDispatch, useAdminState } from '../../context/AdminContext';
import { useAuth } from '../../context/AuthContext';
import { T, rgba } from '../../utils/adminTheme';
import LoginModal from '../Auth/LoginModal';
import LoadingSpinner from '../UI/LoadingSpinner';
import SlidePanel from '../UI/SlidePanel';
import AddBookingModal from '../Modals/AddBookingModal';
import { useSupabaseData } from '../../hooks/useSupabaseData';
import { useNotificationSettings } from '../../hooks/useNotificationSettings';

const AdminDashboard = React.lazy(() => import('./AdminDashboard'));
const AdminBookings = React.lazy(() => import('./AdminBookings'));
const AdminCustomers = React.lazy(() => import('./AdminCustomers'));
const AdminCalendar = React.lazy(() => import('./AdminCalendar'));
const AdminServices = React.lazy(() => import('./AdminServices'));
const AdminLocations = React.lazy(() => import('./AdminLocations'));
const AdminNotifications = React.lazy(() => import('./AdminNotifications'));
const AdminPlanning      = React.lazy(() => import('./AdminPlanning'));
const AdminSettings      = React.lazy(() => import('./AdminSettings'));
const AdminEmployees     = React.lazy(() => import('./AdminEmployees'));
const AdminResources     = React.lazy(() => import('./AdminResources'));

type TabId =
  | 'dashboard'
  | 'calendar'
  | 'bookings'
  | 'customers'
  | 'services'
  | 'locations'
  | 'employees'
  | 'resources'
  | 'notifications'
  | 'planning'
  | 'settings';

interface NavItem {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

function Icon({ d, d2, size = 18, color = 'currentColor' }: {
  d: string;
  d2?: string;
  size?: number;
  color?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={d} />
      {d2 && <path d={d2} />}
    </svg>
  );
}

const ICONS: Record<TabId, React.ReactNode> = {
  dashboard: <Icon d="M3 3h7v9H3zM14 3h7v5h-7zM14 12h7v9h-7zM3 16h7v5H3z" />,
  calendar: <Icon d="M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" d2="M8 2v4M16 2v4M3 10h18" />,
  bookings: <Icon d="M9 11l3 3 9-9M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />,
  customers: <Icon d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />,
  services: <Icon d="M12 2v6M12 16v6M2 12h6M16 12h6M5 5l4 4M15 15l4 4M5 19l4-4M15 9l4-4" />,
  locations: <Icon d="M12 22s8-7 8-13a8 8 0 0 0-16 0c0 6 8 13 8 13z" d2="M12 9m-3 0a3 3 0 1 0 6 0 3 3 0 0 0-6 0" />,
  employees: <Icon d="M16 11c1.7 0 2.99-1.3 2.99-3S17.7 5 16 5c-1.7 0-3 1.3-3 3s1.3 3 3 3zm-8 0c1.7 0 2.99-1.3 2.99-3S9.7 5 8 5C6.3 5 5 6.3 5 8s1.3 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />,
  resources: <Icon d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" d2="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />,
  notifications: <Icon d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />,
  planning: <Icon d="M12 6v6l4 2" d2="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z" />,
  settings: <Icon d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0 .33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />,
};

const NAV: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: ICONS.dashboard },
  { id: 'calendar', label: 'Kalender', icon: ICONS.calendar },
  { id: 'bookings', label: 'Buchungen', icon: ICONS.bookings },
  { id: 'customers', label: 'Kunden', icon: ICONS.customers },
  { id: 'services', label: 'Services', icon: ICONS.services },
  { id: 'locations', label: 'Standorte', icon: ICONS.locations },
  { id: 'employees',  label: 'Personal',    icon: ICONS.employees },
  { id: 'resources',  label: 'Ressourcen',  icon: ICONS.resources },
  { id: 'notifications', label: 'Mitteilungen', icon: ICONS.notifications },
  { id: 'planning', label: 'Planung', icon: ICONS.planning },
  { id: 'settings', label: 'Einstellungen', icon: ICONS.settings },
];

function SuspenseFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
      <LoadingSpinner label="Laedt..." />
    </div>
  );
}

interface AdminPanelProps {
  onBack?: () => void;
}

const AdminPanel: React.FC<AdminPanelProps> = memo(({ onBack }) => {
  const { tab: adminTab, isAdmin } = useAdminState();
  const { user, logout } = useAuth();
  const dispatch = useAdminDispatch();
  const [showLogin, setShowLogin] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showNewBooking, setShowNewBooking] = useState(false);
  const { services, locations, convertService, convertLocation } = useSupabaseData();
  const { settings: appSettings } = useNotificationSettings();

  const setTab = (id: TabId) => {
    dispatch({ type: 'SET_TAB', payload: id });
    sessionStorage.setItem('adminTab', id);
    setMobileSidebarOpen(false);
  };

  const activeNav = NAV.find(nav => nav.id === adminTab);

  const renderContent = () => {
    const fallback = <SuspenseFallback />;

    switch (adminTab) {
      case 'dashboard':
        return <Suspense fallback={fallback}><AdminDashboard /></Suspense>;
      case 'calendar':
        return <Suspense fallback={fallback}><AdminCalendar /></Suspense>;
      case 'bookings':
        return <Suspense fallback={fallback}><AdminBookings /></Suspense>;
      case 'customers':
        return <Suspense fallback={fallback}><AdminCustomers /></Suspense>;
      case 'services':
        return <Suspense fallback={fallback}><AdminServices /></Suspense>;
      case 'locations':
        return <Suspense fallback={fallback}><AdminLocations /></Suspense>;
      case 'employees':
        return <Suspense fallback={fallback}><AdminEmployees /></Suspense>;
      case 'resources':
        return <Suspense fallback={fallback}><AdminResources /></Suspense>;
      case 'notifications':
        return <Suspense fallback={fallback}><AdminNotifications /></Suspense>;
      case 'planning':
        return <Suspense fallback={fallback}><AdminPlanning /></Suspense>;
      case 'settings':
        return <Suspense fallback={fallback}><AdminSettings onSaveSettings={async () => {}} /></Suspense>;
      default:
        return null;
    }
  };

  const SidebarContent = () => {
    const initials = user?.email?.[0]?.toUpperCase() ?? 'A';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: T.body }}>
        {/* Brand */}
        <div style={{ padding: '14px 10px 18px', borderBottom: `1px solid ${T.lineSoft}`, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: T.r.sm,
              background: T.accent,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: T.body,
              fontWeight: 700,
              fontSize: 16,
            }}>
              T
            </div>
            <div>
              <div style={{ fontFamily: T.body, fontWeight: 700, fontSize: 14, color: T.text, lineHeight: 1, letterSpacing: '-0.01em' }}>
                {appSettings.system.companyName || 'Traumbeauty'}
              </div>
              <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.10em', marginTop: 2 }}>
                STUDIO ADMIN
              </div>
            </div>
          </div>
        </div>

        <nav style={{ flex: 1, padding: '0 8px', display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto' }}>
          {NAV.map(item => {
            const active = adminTab === item.id;

            return (
              <button
                key={item.id}
                onClick={() => setTab(item.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '7px 10px',
                  background: active ? T.surface2 : 'transparent',
                  color: active ? T.text : T.muted,
                  border: 'none',
                  borderRadius: T.r.sm,
                  fontFamily: T.body,
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background .15s, color .15s',
                } as CSSProperties}
                onMouseEnter={event => {
                  if (!active) event.currentTarget.style.color = T.text;
                }}
                onMouseLeave={event => {
                  if (!active) event.currentTarget.style.color = T.muted;
                }}
              >
                <span style={{ color: active ? T.accent : 'currentColor', display: 'flex' }}>
                  {item.icon}
                </span>
                {item.label}
              </button>
            );
          })}
        </nav>

        <div style={{ padding: '12px 10px', borderTop: `1px solid ${T.lineSoft}` }}>
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28,
                height: 28,
                borderRadius: T.r.sm,
                background: rgba(T.accent, 0.15),
                color: T.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: T.body,
                fontWeight: 700,
                fontSize: 12,
                flexShrink: 0,
              }}>
                {initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: T.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {user.email}
                </div>
                <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>Administrator</div>
              </div>
              <button
                onClick={() => logout()}
                title="Abmelden"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: T.muted,
                  padding: 4,
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                }}
                onMouseEnter={event => {
                  event.currentTarget.style.color = T.danger;
                }}
                onMouseLeave={event => {
                  event.currentTarget.style.color = T.muted;
                }}
              >
                <Icon d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" size={16} />
              </button>
            </div>
          ) : isAdmin ? (
            <div style={{ fontSize: 12, color: T.muted, fontFamily: T.body }}>
              Review-Modus aktiv
            </div>
          ) : (
            <button
              onClick={() => setShowLogin(true)}
              style={{
                width: '100%',
                padding: '9px 12px',
                background: T.accent,
                color: '#fff',
                border: 'none',
                borderRadius: T.r.sm,
                fontFamily: T.body,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <Icon d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" size={15} color="#fff" />
              Anmelden
            </button>
          )}
        </div>
      </div>
    );
  };

  const dateStr = new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 4rem)', background: T.bg, fontFamily: T.body }}>
      <aside
        style={{
          display: 'none',
          width: 220,
          flexShrink: 0,
          background: T.surface,
          borderRight: `1px solid ${T.line}`,
        }}
        className="md-sidebar"
      >
        <div style={{ position: 'sticky', top: 0, height: '100vh' }}>
          <SidebarContent />
        </div>
      </aside>

      {mobileSidebarOpen && (
        <>
          <div
            onClick={() => setMobileSidebarOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 40 }}
          />
          <aside
            style={{
              position: 'fixed',
              left: 0,
              top: 0,
              bottom: 0,
              width: 260,
              background: T.surface,
              zIndex: 50,
              boxShadow: '4px 0 24px rgba(0,0,0,0.12)',
              animation: 'slideInLeft .2s ease',
            }}
          >
            <button
              onClick={() => setMobileSidebarOpen(false)}
              style={{
                position: 'absolute',
                top: 14,
                right: 14,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: T.muted,
                padding: 4,
              }}
            >
              <Icon d="M18 6L6 18M6 6l12 12" size={18} />
            </button>
            <SidebarContent />
          </aside>
        </>
      )}

      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            background: T.surface,
            borderBottom: `1px solid ${T.line}`,
            position: 'sticky',
            top: 0,
            zIndex: 30,
          }}
          className="mobile-topbar"
        >
          <button
            onClick={() => setMobileSidebarOpen(true)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: T.text, padding: 4 }}
          >
            <Icon d="M3 6h18M3 12h18M3 18h18" size={20} />
          </button>
          <span style={{ fontFamily: T.body, fontWeight: 700, fontSize: 15, color: T.text }}>
            {activeNav?.label || 'Admin'}
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 20px',
            background: T.surface,
            borderBottom: `1px solid ${T.line}`,
            gap: 16,
          }}
          className="desktop-topbar"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            {onBack && (
              <button
                onClick={onBack}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: T.muted,
                  fontFamily: T.body,
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 0',
                  flexShrink: 0,
                }}
                onMouseEnter={e => (e.currentTarget.style.color = T.text)}
                onMouseLeave={e => (e.currentTarget.style.color = T.muted)}
              >
                <Icon d="M19 12H5M12 5l-7 7 7 7" size={14} />
                Buchung
              </button>
            )}
            <div>
              <h1 style={{
                fontFamily: T.body,
                fontWeight: 700,
                fontSize: 16,
                color: T.text,
                margin: 0,
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}>
                {activeNav?.label || 'Admin Panel'}
              </h1>
              <p style={{ fontSize: 10, color: T.muted, margin: '1px 0 0', fontFamily: T.mono }}>
                {dateStr}
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowNewBooking(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 16px',
              background: T.accent,
              color: '#fff',
              border: 'none',
              borderRadius: T.r.sm,
              fontFamily: T.body,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Icon d="M12 5v14M5 12h14" size={15} color="#fff" />
            Buchung
          </button>
        </div>

        <div className="admin-content" style={{ flex: 1, padding: '20px 24px', overflow: 'auto' }}>
          {renderContent()}
        </div>
      </main>

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}

      <SlidePanel
        open={showNewBooking}
        onClose={() => setShowNewBooking(false)}
        title="Neuer Termin"
        width={520}
      >
        <AddBookingModal
          onClose={() => setShowNewBooking(false)}
          services={services.map(convertService)}
          locations={locations.map(convertLocation)}
          onBookingCreated={() => setShowNewBooking(false)}
        />
      </SlidePanel>

      <style>{`
        @media (min-width: 768px) {
          .md-sidebar { display: block !important; }
          .mobile-topbar { display: none !important; }
        }
        @media (max-width: 767px) {
          .desktop-topbar { display: none !important; }
          .admin-content { padding: 16px !important; }
        }
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
});

AdminPanel.displayName = 'AdminPanel';

export default AdminPanel;
